import { ChevronLeft, ChevronRight, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/shadcn/button';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/shadcn/popover';
import { Progress } from '@/components/shadcn/progress';

import { SPOTLIGHT_PADDING, TOOLTIP_GAP, TRANSITION } from './../constants';
import type { Rect, Step } from './../types';

export default function Tooltip({
  step,
  rect,
  currentIndex,
  totalSteps,
  handleNext,
  handlePrevious,
  handleClose,
}: {
  step: Step;
  rect: Rect;
  currentIndex: number;
  totalSteps: number;
  handleNext: VoidFunction;
  handlePrevious: VoidFunction;
  handleClose: VoidFunction;
}) {
  const { t } = useTranslation();

  const isLastStep = currentIndex + 1 === totalSteps;

  return (
    <Popover open>
      <PopoverAnchor
        className="pointer-events-none fixed"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          transition: TRANSITION,
        }}
      />

      <PopoverContent
        side={step.placement}
        align="center"
        sideOffset={SPOTLIGHT_PADDING + TOOLTIP_GAP}
        collisionPadding={8}
        className="z-10000 flex w-72 flex-col gap-4 p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="-mt-2 -mr-2 -mb-2 flex items-center gap-2">
          <Progress
            value={((currentIndex + 1) / totalSteps) * 100}
            className="h-0.5"
          />

          <Button variant="ghost" size="icon-sm" onClick={handleClose}>
            <XIcon />
          </Button>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-sm leading-none font-semibold">{step.title}</h3>
          <p className="text-muted-foreground text-sm">{step.description}</p>
        </div>

        <div className="flex items-center justify-between gap-4">
          <Button
            variant="ghost"
            size="xs"
            disabled={currentIndex === 0}
            onClick={handlePrevious}
          >
            <ChevronLeft className="size-3" />

            {t('ONBOARDING_PREV')}
          </Button>

          <div className="flex items-center gap-4">
            <Button variant="default" size="xs" onClick={handleNext}>
              {isLastStep ? t('ONBOARDING_FINISH') : t('ONBOARDING_NEXT')}

              {!isLastStep && <ChevronRight className="size-3" />}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
